import React, { useState, useEffect } from "react";
import Markdown from "markdown-to-jsx";
import { useDispatch, useSelector } from "react-redux";

import { Language, Utils } from "../../../utils";
import { user as userActions } from "../../../actions";
import { Button, ErrorIcon } from "../../../components";

import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import makeClasses from "../styles";
import clsx from "clsx";
import { Checkbox, InputLabel } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

const StepFive = ({ traceId, nextStep }) => {
  const dispatch = useDispatch();
  const classes = makeClasses();

  const { loading, languageTexts } = useSelector((state) => ({
    loading: state.user.loading,
    languageTexts: state.language.texts || {},
  }));
  const i18n = Language(languageTexts);

  const [countries, setCountries] = useState([]);
  const [inputPhoneCharacteristic, setInputPhoneCharacteristic] =
    useState(null);
  const [inputPhone, setInputPhone] = useState("");
  const [inputPhoneConfirm, setInputPhoneConfirm] = useState("");
  const [hasPhoneError, setHasPhoneError] = useState(false);
  const [hasPhoneMismatchError, setHasPhoneMismatchError] = useState(false);
  const [acceptedHandyTermsAndConditions, setAcceptedHandyTermsAndConditions] =
    useState(false);
  const [acceptedPaigoTermsAndConditions, setAcceptedPaigoTermsAndConditions] =
    useState(false);

  const canMoveNext =
    inputPhoneCharacteristic !== null &&
    inputPhone.trim().length > 0 &&
    inputPhoneConfirm.trim().length > 0 &&
    !hasPhoneError &&
    !hasPhoneMismatchError &&
    acceptedHandyTermsAndConditions &&
    acceptedPaigoTermsAndConditions;

  useEffect(() => {
    dispatch(
      userActions.getCountries((countriesList) => {
        setCountries(countriesList);
        countriesList.forEach((country) => {
          if (country.alphaCode2 === "UY") {
            setInputPhoneCharacteristic(country.id);
          }
        });
      }),
    );
  }, []);

  const getPhoneCharacteristicLabel = (option) => {
    const found = countries.find((c) => c.id === option);
    if (found) return `${found.name} ${found.dialingCode}`;
    return "";
  };

  const validatePhone = (phone, characteristic) => {
    const countryObj = countries.find((c) => c.id === characteristic);
    if (!countryObj || !phone) return true;
    const completePhone = countryObj.dialingCode + phone.trim();
    return !Utils.validatePhoneNumber(completePhone);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    if (value.length <= 15 && /^\d*$/.test(value)) {
      setInputPhone(value);
      setHasPhoneError(
        value.length > 0 && validatePhone(value, inputPhoneCharacteristic),
      );
      setHasPhoneMismatchError(
        inputPhoneConfirm.length > 0 &&
          value.trim() !== inputPhoneConfirm.trim(),
      );
    }
  };

  const handlePhoneConfirmChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setInputPhoneConfirm(value);
      setHasPhoneMismatchError(value.trim() !== inputPhone.trim());
    }
  };

  const handleCharacteristicChange = (e, newValue) => {
    setInputPhoneCharacteristic(newValue);
    if (inputPhone) {
      setHasPhoneError(validatePhone(inputPhone, newValue));
    }
  };

  const handleSubmit = () => {
    const phoneInvalid = validatePhone(inputPhone, inputPhoneCharacteristic);
    const mismatch = inputPhone.trim() !== inputPhoneConfirm.trim();
    if (phoneInvalid) setHasPhoneError(true);
    if (mismatch) setHasPhoneMismatchError(true);
    if (phoneInvalid || mismatch) return;

    const countryObj = countries.find((c) => c.id === inputPhoneCharacteristic);

    dispatch(
      userActions.affiliationRequestPhoneVerificationCodeV2(
        traceId,
        { countryId: countryObj.id, number: inputPhone.trim() },
        (step3TraceId) => {
          if (step3TraceId !== false && step3TraceId) {
            nextStep({
              phone: inputPhone.trim(),
              phoneCountryId: countryObj.id,
              affiliationStep3TraceId: step3TraceId,
            });
          }
        },
      ),
    );
  };

  return (
    <React.Fragment>
      <div className={classes.formContent}>
        <div className={classes.formTitleContainer}>
          <div className={classes.formTitle}>
            {i18n.get("Register.StepFormTitle")}
          </div>
          <div className={classes.formText}>
            {i18n.get("Register.Step5PhoneFormText")}
          </div>
        </div>
        <form
          layout="vertical"
          onSubmit={handleSubmit}
          className={classes.form}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InputLabel className={classes.formSelectLabel}>
                {i18n.get("Register.InputPhoneCharacteristicLabel")}
              </InputLabel>
              <Autocomplete
                options={countries.map((c) => c.id)}
                getOptionLabel={getPhoneCharacteristicLabel}
                onChange={handleCharacteristicChange}
                classes={{ inputRoot: classes.formInputSelect }}
                value={inputPhoneCharacteristic}
                renderInput={(params) => (
                  <TextField
                    className={classes.formInput}
                    classes={{ root: classes.formInput }}
                    variant="outlined"
                    placeholder={i18n.get("Register.InputPhoneCharacteristic")}
                    {...params}
                  />
                )}
              />
            </Grid>
            <Grid className={classes.inputWithErrorContainer} item xs={12}>
              <InputLabel className={classes.formSelectLabel}>
                {i18n.get("Register.InputPhoneLabel")}
              </InputLabel>
              <TextField
                error={hasPhoneError}
                placeholder={i18n.get("Register.InputPhoneTitle")}
                className={classes.formInput}
                onChange={handlePhoneChange}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputPhone}
                inputProps={{
                  inputMode: "numeric",
                  autoComplete: "off",
                  onCopy: (e) => e.preventDefault(),
                  onPaste: (e) => e.preventDefault(),
                }}
              />
              {hasPhoneError && (
                <div className={classes.errorMessageContainer}>
                  <ErrorIcon />
                  <div className={classes.errorMessage}>
                    {i18n.get("Register.PhoneErrorMessage")}
                  </div>
                </div>
              )}
            </Grid>
            <Grid className={classes.inputWithErrorContainer} item xs={12}>
              <InputLabel className={classes.formSelectLabel}>
                {i18n.get("Register.InputPhoneConfirmLabel")}
              </InputLabel>
              <TextField
                error={hasPhoneMismatchError}
                placeholder={i18n.get("Register.InputPhoneConfirmTitle")}
                className={classes.formInput}
                onChange={handlePhoneConfirmChange}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputPhoneConfirm}
                inputProps={{
                  inputMode: "numeric",
                  autoComplete: "off",
                  onCopy: (e) => e.preventDefault(),
                  onPaste: (e) => e.preventDefault(),
                }}
              />
              {hasPhoneMismatchError && (
                <div className={classes.errorMessageContainer}>
                  <ErrorIcon />
                  <div className={classes.errorMessage}>
                    {i18n.get("Register.PhoneMismatchErrorMessage")}
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </form>
      </div>
      <div className={classes.termsWrapper}>
        <div className={classes.termsContainer}>
          <Checkbox
            classes={{ root: classes.checkboxRoot }}
            checked={acceptedHandyTermsAndConditions}
            onChange={() => setAcceptedHandyTermsAndConditions((prev) => !prev)}
          />
          <Markdown className={classes.termsLabel}>
            {i18n.get("Register.HandyTyC")}
          </Markdown>
        </div>
        <div className={classes.termsContainer}>
          <Checkbox
            classes={{ root: classes.checkboxRoot }}
            checked={acceptedPaigoTermsAndConditions}
            onChange={() => setAcceptedPaigoTermsAndConditions((prev) => !prev)}
          />
          <Markdown className={classes.termsLabel}>
            {i18n.get("Register.PaigoTyCLabel")}
          </Markdown>
        </div>
      </div>
      <div
        className={clsx(
          classes.formSubmitContainer,
          classes.formSubmitContainerWithOneButton,
        )}>
        <Button
          className={classes.formSubmitButton}
          onClick={handleSubmit}
          disabled={loading || !canMoveNext}>
          {i18n.get("Register.NextButton")}
        </Button>
      </div>
    </React.Fragment>
  );
};

StepFive.id = "com.Handy.Register.StepFive";
export default StepFive;
